import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { fmtNumber, fmtPercent, fmtDuration } from '../shared/format';
import {
  sortRows, downloadCsv, downloadPdf, makeExport,
  CHANNEL_COLORS, TREND_METRICS,
  SortableHeader, PaginationFooter, Sparkline, TableSkeleton, SectionInsightCard, ViewToggle,
} from '../shared';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonChart } from '../../shared/Skeleton';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import useTablePage from '../../../hooks/useTablePage';

/* ============================= Source / Medium table ============================= */
const SourcesTable = ({ data, trendData, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const perPage = 10;

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(row => (row.source || '').toLowerCase().includes(query) || (row.medium || '').toLowerCase().includes(query));
  }, [data, searchQuery]);

  const sparklines = useMemo(() => {
    if (!trendData || trendData.length === 0) return {};
    const map = {};
    for (const row of trendData) {
      const key = `${row.source || 'Unknown'} / ${row.medium || 'Unknown'}`;
      if (!map[key]) map[key] = [];
      map[key].push(row.sessions);
    }
    return map;
  }, [trendData]);

  const totalSessions = useMemo(() => (filtered || []).reduce((sum, row) => sum + (row.sessions || 0), 0), [filtered]);

  const enriched = useMemo(() =>
    (filtered || []).map((row) => ({
      ...row,
      source_medium: `${row.source || 'Unknown'} / ${row.medium || 'Unknown'}`,
      pct_of_total: totalSessions > 0 ? (row.sessions / totalSessions) * 100 : 0,
    })),
    [filtered, totalSessions],
  );

  const sorted = useMemo(() => sortRows(enriched, sort.key, sort.dir), [enriched, sort]);
  const totalItems = sorted.length;
  // Reset on data/search/sort change, and clamp so a stale page can never render
  // an empty table (see useTablePage).
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(totalItems, perPage, [data, searchQuery, sort]);
  const paged = sorted.slice(sliceStart, sliceEnd);

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No source/medium data for this period." padding="py-12" />;
  }

  return (
    <div>
      <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{totalItems} source{totalItems !== 1 ? 's' : ''}</div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm table-fixed min-w-[1000px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Source / Medium" sortKey="source_medium" currentSort={sort} onSort={handleSort} className="w-[200px]" />
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 w-[80px]">Trend</th>
              <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} className="w-[90px]" />
              <SortableHeader label="% of Total" sortKey="pct_of_total" currentSort={sort} onSort={handleSort} className="w-[85px]" />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} className="w-[80px]" />
              <SortableHeader label="New Users" sortKey="new_users" currentSort={sort} onSort={handleSort} className="w-[90px]" />
              <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} className="w-[95px]" />
              <SortableHeader label="Avg. Duration" sortKey="avg_session_duration" currentSort={sort} onSort={handleSort} className="w-[100px]" />
              <SortableHeader label="Engagement Rate" sortKey="engagement_rate" currentSort={sort} onSort={handleSort} className="w-[110px]" />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} className="w-[95px]" />
            </tr>
          </thead>
          <tbody>
            {paged.map((row) => (
              <tr key={row.source_medium} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Source / Medium" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap truncate">{row.source_medium}</td>
                <td data-label="Trend" className="px-3 py-2.5"><Sparkline values={sparklines[row.source_medium]} /></td>
                <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.sessions)}</td>
                <td data-label="% of Total" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.pct_of_total.toFixed(1)}%</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.users)}</td>
                <td data-label="New Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.new_users)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtPercent(row.bounce_rate)}</td>
                <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtDuration(row.avg_session_duration)}</td>
                <td data-label="Engagement Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtPercent(row.engagement_rate)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.conversions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="sources" />
    </div>
  );
};

/* ============================= Campaigns sub-tab ============================= */
const CampaignsTable = ({ data, loading, error }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const perPage = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const sorted = useMemo(() => sortRows(data || [], sort.key, sort.dir), [data, sort]);
  const totalItems = sorted.length;
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(totalItems, perPage, [data, sort]);
  const paged = sorted.slice(sliceStart, sliceEnd);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No campaign data for this period." padding="py-12" />;
  }

  return (
    <div>
      <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{totalItems} campaign{totalItems !== 1 ? 's' : ''}</div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm table-fixed min-w-[800px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Campaign" sortKey="campaign" currentSort={sort} onSort={handleSort} className="w-[220px]" />
              <SortableHeader label="Source" sortKey="source" currentSort={sort} onSort={handleSort} className="w-[120px]" />
              <SortableHeader label="Medium" sortKey="medium" currentSort={sort} onSort={handleSort} className="w-[100px]" />
              <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} className="w-[90px]" />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} className="w-[80px]" />
              <SortableHeader label="Engaged Sessions" sortKey="engaged_sessions" currentSort={sort} onSort={handleSort} className="w-[120px]" />
              <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} className="w-[95px]" />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} className="w-[95px]" />
            </tr>
          </thead>
          <tbody>
            {paged.map((row, index) => (
              <tr key={`${row.campaign}-${row.source}-${row.medium}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Campaign" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 font-medium whitespace-nowrap truncate">{row.campaign || '—'}</td>
                <td data-label="Source" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">{row.source || '—'}</td>
                <td data-label="Medium" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.medium || '—'}</td>
                <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.sessions)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.users)}</td>
                <td data-label="Engaged Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.engaged_sessions)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtPercent(row.bounce_rate)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.conversions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="campaigns" />
    </div>
  );
};

/* ============================= Sources trend chart (top 5 source/medium) ============================= */
const aggregateSourceByWeek = (rows) => {
  const weeks = {};
  for (const row of rows) {
    const date = new Date(row.date + 'T00:00:00');
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(date);
    weekStart.setDate(diff);
    const key = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
    const sourceKey = `${row.source || 'Unknown'} / ${row.medium || 'Unknown'}`;
    const weekKey = `${key}|${sourceKey}`;
    if (!weeks[weekKey]) weeks[weekKey] = { date: key, _key: sourceKey, sessions: 0, users: 0, conversions: 0 };
    weeks[weekKey].sessions += row.sessions || 0;
    weeks[weekKey].users += row.users || 0;
    weeks[weekKey].conversions += row.conversions || 0;
  }
  return Object.values(weeks);
};

const SourcesTrendChart = ({ sourceMediumData, sourceMediumTrendData, loading, error }) => {
  const [metric, setMetric] = useState('sessions');

  const dates = useMemo(() => {
    if (!sourceMediumTrendData || sourceMediumTrendData.length === 0) return [];
    return [...new Set(sourceMediumTrendData.map((row) => row.date))].sort();
  }, [sourceMediumTrendData]);

  const useWeekly = dates.length > 60;

  const topPairs = useMemo(() => {
    if (!sourceMediumData || sourceMediumData.length === 0) return [];
    return [...sourceMediumData]
      .sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0))
      .slice(0, 5)
      .map((row) => `${row.source || 'Unknown'} / ${row.medium || 'Unknown'}`);
  }, [sourceMediumData]);

  const chartData = useMemo(() => {
    if (!sourceMediumTrendData || sourceMediumTrendData.length === 0 || topPairs.length === 0) return [];
    const withKey = sourceMediumTrendData.map((row) => ({ ...row, _key: `${row.source || 'Unknown'} / ${row.medium || 'Unknown'}` }));
    const rows = useWeekly ? aggregateSourceByWeek(sourceMediumTrendData) : withKey;
    const byDate = {};
    for (const row of rows) {
      const sourceKey = row._key;
      if (!topPairs.includes(sourceKey)) continue;
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      byDate[row.date][sourceKey] = (byDate[row.date][sourceKey] || 0) + (row[metric] || 0);
    }
    return Object.values(byDate)
      .sort((rowA, rowB) => rowA.date.localeCompare(rowB.date))
      .map((row) => ({
        ...row,
        label: new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', useWeekly ? { month: 'short', day: 'numeric' } : { weekday: 'short', month: 'short', day: 'numeric' }),
      }));
  }, [sourceMediumTrendData, topPairs, metric, useWeekly]);

  const distinctWeeks = useMemo(() => {
    if (dates.length === 0) return 0;
    const weekSet = new Set();
    for (const date of dates) {
      const dateObj = new Date(date + 'T00:00:00');
      const year = dateObj.getFullYear();
      const jan1 = new Date(year, 0, 1);
      const weekNum = Math.ceil(((dateObj - jan1) / 86400000 + jan1.getDay() + 1) / 7);
      weekSet.add(`${year}-W${weekNum}`);
    }
    return weekSet.size;
  }, [dates]);

  if (error) return <ErrorState message={error} />;

  if (loading) {
    return <SkeletonChart height={280} />;
  }

  if (!sourceMediumTrendData || sourceMediumTrendData.length === 0) {
    return <EmptyState message="No source trend data for this period." padding="py-12" />;
  }

  if (distinctWeeks < 2) {
    return (
      <EmptyState message="Trend data will be available once you have multiple weeks of analytics data." padding="py-12" />
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1 mb-4">
        {TREND_METRICS.map((metricOption) => (
          <button
            key={metricOption.key}
            type="button"
            onClick={() => setMetric(metricOption.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              metric === metricOption.key
                ? 'text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            style={metric === metricOption.key ? { backgroundColor: metricOption.color } : undefined}
          >
            {metricOption.label}
          </button>
        ))}
        {useWeekly && (
          <span className="ml-3 text-[11px] text-gray-400 dark:text-gray-500 italic">Aggregated by week</span>
        )}
      </div>

      <div className="rotate-x-labels">
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            interval={Math.max(0, Math.floor(chartData.length / 10) - 1)}
            angle={-30}
            textAnchor="end"
            height={50}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
            width={50}
          />
          <ThemedChartTooltip
            formatter={(value, name) => [Number(value).toLocaleString(), name]}
            labelFormatter={(label) => label}
          />
          {topPairs.map((pair, index) => (
            <Line
              key={pair}
              type="monotone"
              dataKey={pair}
              name={pair}
              stroke={CHANNEL_COLORS[index % CHANNEL_COLORS.length]}
              strokeWidth={2}
              dot={chartData.length <= 31}
              activeDot={{ r: 4, strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3 px-2">
        {topPairs.map((pair, index) => (
          <div key={pair} className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full inline-block" style={{ backgroundColor: CHANNEL_COLORS[index % CHANNEL_COLORS.length] }} />
            <span className="text-xs text-gray-600 dark:text-gray-400">{pair}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================= Insight summary ============================= */
export const getSourcesSummary = (sourceMediumData, trendData) => {
  if (!sourceMediumData || sourceMediumData.length === 0) return { summary: [], warnings: [] };
  const summary = [];
  const warnings = [];
  const total = sourceMediumData.reduce((sum, row) => sum + (row.sessions || 0), 0);
  const sorted = [...sourceMediumData].sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0));
  if (sorted[0] && total > 0) {
    const pct = ((sorted[0].sessions / total) * 100).toFixed(1);
    summary.push(`Top source is ${sorted[0].source || 'Unknown'} / ${sorted[0].medium || 'Unknown'}, driving ${pct}% of sessions.`);
  }
  if (trendData && trendData.length >= 4) {
    const allDates = [...new Set(trendData.map(row => row.date))].sort();
    if (allDates.length >= 4) {
      const mid = Math.floor(allDates.length / 2);
      const firstHalfDates = new Set(allDates.slice(0, mid));
      const secondHalfDates = new Set(allDates.slice(mid));
      const byKey = {};
      for (const row of trendData) {
        const key = `${row.source || 'Unknown'} / ${row.medium || 'Unknown'}`;
        if (!byKey[key]) byKey[key] = { firstHalf: 0, secondHalf: 0 };
        if (firstHalfDates.has(row.date)) byKey[key].firstHalf += row.sessions || 0;
        if (secondHalfDates.has(row.date)) byKey[key].secondHalf += row.sessions || 0;
      }
      const newSources = Object.entries(byKey)
        .filter(([, counts]) => counts.firstHalf === 0 && counts.secondHalf >= 20)
        .sort((entryA, entryB) => entryB[1].secondHalf - entryA[1].secondHalf)
        .slice(0, 5);
      if (newSources.length > 0) {
        summary.push(`New sources appeared: ${newSources.map(([key, counts]) => `${key} (${counts.secondHalf.toLocaleString()} sessions)`).join(', ')}.`);
      }
    }
  }
  return { summary, warnings };
};

/* ============================= Export helpers ============================= */
// One column spec drives CSV + PDF (can't drift). CSV emits raw values + full
// headers; PDF emits formatted strings + abbreviated headers.
const SOURCES_EXPORT = makeExport([
  { header: 'Source', get: (row) => row.source },
  { header: 'Medium', get: (row) => row.medium },
  { header: 'Sessions', get: (row) => row.sessions, pdf: (row) => fmtNumber(row.sessions) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'New Users', get: (row) => row.new_users, pdf: (row) => fmtNumber(row.new_users) },
  { header: 'Bounce Rate', pdfHeader: 'Bounce', get: (row) => (row.bounce_rate != null ? (row.bounce_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.bounce_rate) },
  { header: 'Engagement Rate', pdfHeader: 'Engage', get: (row) => (row.engagement_rate != null ? (row.engagement_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.engagement_rate) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (row) => row.conversions, pdf: (row) => fmtNumber(row.conversions) },
]);

export const exportSourcesCsv = (sourceMediumData, rangeStart, rangeEnd) => {
  if (!sourceMediumData?.length) return;
  downloadCsv(SOURCES_EXPORT.headers, sourceMediumData.map(SOURCES_EXPORT.toCsvRow), `ga4-sources-${rangeStart}-to-${rangeEnd}.csv`);
};

export const exportSourcesPdf = (sourceMediumData, rangeStart, rangeEnd) => {
  if (!sourceMediumData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  downloadPdf('Sources', SOURCES_EXPORT.pdfHeaders, sourceMediumData.map(SOURCES_EXPORT.toPdfRow), `ga4-sources-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

/* ============================= Tab component ============================= */

const SourcesTabContent = ({ sourceMediumData, sourceMediumLoading, sourceMediumError, sourceMediumTrendData, campaignsData, campaignsLoading, campaignsError, searchQuery }) => {
  const [subTab, setSubTab] = useState('source_medium');

  const subTabs = [
    { id: 'source_medium', label: 'Source / Medium' },
    { id: 'campaigns', label: 'Campaigns' },
  ];

  return (
    <div>
      <SectionInsightCard title="Sources Insight" {...getSourcesSummary(sourceMediumData, sourceMediumTrendData)} />
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1.5">
          {subTabs.map((subTabItem) => (
            <button
              key={subTabItem.id}
              type="button"
              onClick={() => setSubTab(subTabItem.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                subTab === subTabItem.id
                  ? 'bg-[#32B1FF]/10 text-[#32B1FF] border border-[#32B1FF]/30'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
              }`}
            >
              {subTabItem.label}
            </button>
          ))}
        </div>
      </div>

      {subTab === 'source_medium' && (
        <SourcesTable data={sourceMediumData} trendData={sourceMediumTrendData} loading={sourceMediumLoading} error={sourceMediumError} searchQuery={searchQuery} />
      )}
      {subTab === 'campaigns' && (
        <CampaignsTable data={campaignsData} loading={campaignsLoading} error={campaignsError} />
      )}
    </div>
  );
};

const SourcesTab = ({
  sourceMediumData, sourceMediumLoading, sourceMediumError, sourceMediumTrendData,
  campaignsData, campaignsLoading, campaignsError, searchQuery,
}) => {
  const [view, setView] = useState('table');

  return (
    <div>
      <ViewToggle view={view} onChange={setView} />
      {view === 'table' ? (
        <SourcesTabContent
          sourceMediumData={sourceMediumData}
          sourceMediumLoading={sourceMediumLoading}
          sourceMediumError={sourceMediumError}
          sourceMediumTrendData={sourceMediumTrendData}
          campaignsData={campaignsData}
          campaignsLoading={campaignsLoading}
          campaignsError={campaignsError}
          searchQuery={searchQuery}
        />
      ) : (
        <SourcesTrendChart
          sourceMediumData={sourceMediumData}
          sourceMediumTrendData={sourceMediumTrendData}
          loading={sourceMediumLoading}
          error={sourceMediumError}
        />
      )}
    </div>
  );
};

export default SourcesTab;
